import { DateTime } from 'luxon';
import {
  Reservation,
  TripLeg,
  TripLegAddress,
} from '../data/models/reservation.model';
import {
  TransportPassenger,
  TransportReservation,
  TransportReservationResponse,
  TransportTripLeg,
} from '../data/models/transport-reservation.model';
import { isNil, omitBy } from 'lodash';

function mapTripLegAddress(
  addressLine1: string,
  addressLine2: string,
  city: string,
  stateCode: string,
  zipCode: string,
  latitude: string,
  longitude: string,
  timeZoneIANA: string,
  county?: string,
): TripLegAddress {
  return omitBy(
    {
      addressLine1,
      addressLine2: addressLine2 || undefined,
      city,
      stateCode,
      zipCode,
      county,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
      timeZone: timeZoneIANA,
    },
    isNil,
  ) as unknown as TripLegAddress;
}

function formatCoordinate(value: number | string | undefined | null): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

export function mergeTripLegIntoTransportTripLeg(
  existing: TransportTripLeg,
  updated: TripLeg,
): TransportTripLeg {
  const synced = updated.tripLeg;
  const pickUp = updated.pickUpAddress;
  const dropOff = updated.dropOffAddress;

  return {
    ...existing,
    tripLegID: updated.tripLegID ?? synced?.tripLegID ?? existing.tripLegID,
    tripLegReferenceID:
      updated.tripLegReferenceID ??
      synced?.tripLegReferenceID ??
      existing.tripLegReferenceID,
    tripLegSequence: updated.tripLegSequence ?? existing.tripLegSequence,
    appointmentDate:
      updated.appointmentDate ??
      synced?.appointmentDate ??
      existing.appointmentDate,
    appointmentTimeUTC:
      updated.appointmentTimeUTC ??
      synced?.appointmentTimeUTC ??
      existing.appointmentTimeUTC,
    pickUpDueTimeUTC:
      updated.pickUpDueTimeUTC ??
      synced?.pickUpDueTimeUTC ??
      existing.pickUpDueTimeUTC,
    transportationProviderID:
      updated.transportationProviderID ??
      synced?.transportationProviderID ??
      existing.transportationProviderID,
    driverID: updated.driverID ?? synced?.driverID ?? existing.driverID,
    vehicleID: synced?.vehicleID ?? existing.vehicleID,
    isWillCall: updated.isWillCall,
    companionCount: updated.companionCount,
    isUnderDiscussion:
      updated.isUnderDiscussion ??
      synced?.isUnderDiscussion ??
      existing.isUnderDiscussion,
    isException:
      updated.isException ?? synced?.isException ?? existing.isException,
    isEscalated:
      updated.isEscalated ?? synced?.isEscalated ?? existing.isEscalated,
    isInternal: updated.isInternal ?? synced?.isInternal ?? existing.isInternal,
    estimatedDistanceMiles:
      updated.estimatedDistanceMiles ??
      synced?.estimatedDistanceMiles ??
      existing.estimatedDistanceMiles,
    invoiceDistanceMiles:
      updated.invoiceDistanceMiles ??
      synced?.invoiceDistanceMiles ??
      existing.invoiceDistanceMiles,
    payableAmount:
      updated.payableAmount ?? synced?.payableAmount ?? existing.payableAmount,
    tripLegStatusID: synced?.tripLegStatusID ?? existing.tripLegStatusID,
    tripLegState:
      updated.state ?? synced?.tripLegState ?? existing.tripLegState,
    tripLegStatusBC:
      updated.tripLegStatusBC ??
      updated.status?.toUpperCase() ??
      existing.tripLegStatusBC,
    driverWaitTimeSeconds:
      synced?.driverWaitTimeSeconds ?? existing.driverWaitTimeSeconds,
    transportationTypeID:
      synced?.transportationTypeID ?? existing.transportationTypeID,
    pickUpNotes:
      updated.pickUpNote ?? synced?.pickUpNotes ?? existing.pickUpNotes,
    dropOffNotes:
      updated.dropOffNote ?? synced?.dropOffNotes ?? existing.dropOffNotes,
    pickUpAddressLine1:
      synced?.pickUpAddressLine1 ??
      pickUp?.addressLine1 ??
      existing.pickUpAddressLine1,
    pickUpAddressLine2:
      synced?.pickUpAddressLine2 ??
      pickUp?.addressLine2 ??
      existing.pickUpAddressLine2,
    pickUpCity: synced?.pickUpCity ?? pickUp?.city ?? existing.pickUpCity,
    pickUpStateCode:
      synced?.pickUpStateCode ?? pickUp?.stateCode ?? existing.pickUpStateCode,
    pickUpZipCode:
      synced?.pickUpZipCode ?? pickUp?.zipCode ?? existing.pickUpZipCode,
    pickUpCounty:
      synced?.pickUpCounty ?? pickUp?.county ?? existing.pickUpCounty,
    pickUpAddressLatitude:
      synced?.pickUpAddressLatitude ??
      (formatCoordinate(pickUp?.latitude) || existing.pickUpAddressLatitude),
    pickUpAddressLongitude:
      synced?.pickUpAddressLongitude ??
      (formatCoordinate(pickUp?.longitude) || existing.pickUpAddressLongitude),
    pickUpPassengerPhoneNumber:
      updated.pickUpPhone?.phoneNumber ??
      synced?.pickUpPassengerPhoneNumber ??
      existing.pickUpPassengerPhoneNumber,
    pickUpPassengerPhoneExtension:
      updated.pickUpPhone?.phoneExtension ??
      synced?.pickUpPassengerPhoneExtension ??
      existing.pickUpPassengerPhoneExtension,
    dropOffAddressLine1:
      synced?.dropOffAddressLine1 ??
      dropOff?.addressLine1 ??
      existing.dropOffAddressLine1,
    dropOffAddressLine2:
      synced?.dropOffAddressLine2 ??
      dropOff?.addressLine2 ??
      existing.dropOffAddressLine2,
    dropOffCity: synced?.dropOffCity ?? dropOff?.city ?? existing.dropOffCity,
    dropOffStateCode:
      synced?.dropOffStateCode ??
      dropOff?.stateCode ??
      existing.dropOffStateCode,
    dropOffZipCode:
      synced?.dropOffZipCode ?? dropOff?.zipCode ?? existing.dropOffZipCode,
    dropOffCounty:
      synced?.dropOffCounty ?? dropOff?.county ?? existing.dropOffCounty,
    dropOffAddressLatitude:
      synced?.dropOffAddressLatitude ??
      (formatCoordinate(dropOff?.latitude) || existing.dropOffAddressLatitude),
    dropOffAddressLongitude:
      synced?.dropOffAddressLongitude ??
      (formatCoordinate(dropOff?.longitude) ||
        existing.dropOffAddressLongitude),
    dropOffPassengerPhoneNumber:
      updated.dropOffPhone?.phoneNumber ??
      synced?.dropOffPassengerPhoneNumber ??
      existing.dropOffPassengerPhoneNumber,
    dropOffPassengerPhoneExtension:
      updated.dropOffPhone?.phoneExtension ??
      synced?.dropOffPassengerPhoneExtension ??
      existing.dropOffPassengerPhoneExtension,
    pickUpAddressTimeZoneIANA:
      updated.pickUpAddressTimeZoneIANA ??
      pickUp?.timeZone ??
      existing.pickUpAddressTimeZoneIANA,
    dropOffAddressTimeZoneIANA:
      dropOff?.timeZone ?? existing.dropOffAddressTimeZoneIANA,
  };
}

export function mapTransportTripLeg(
  leg: TransportTripLeg,
  passenger: TransportPassenger | null,
): TripLeg {
  return {
    tripLegID: leg.tripLegID,
    tripLegReferenceID: leg.tripLegReferenceID,
    tripLegSequence: leg.tripLegSequence,
    appointmentDate: leg.appointmentDate,
    appointmentDateTime: leg.appointmentTimeUTC
      ? (DateTime.fromISO(leg.appointmentTimeUTC)
          .setZone(leg.appointmentTimeZoneIANA)
          .toISO({ includeOffset: false }) ?? undefined)
      : undefined,
    appointmentTimeUTC: leg.appointmentTimeUTC ?? undefined,
    pickUpDueTimeUTC: leg.pickUpDueTimeUTC ?? undefined,
    pickUpAddressTimeZoneIANA: leg.pickUpAddressTimeZoneIANA,
    passengerUUID: passenger?.passengerUUID ?? '',
    purposeCodeUUID: leg.clientPurposeCode?.clientPurposeCodeUUID ?? '',
    isWillCall: leg.isWillCall,
    companionCount: leg.companionCount,
    isException: leg.isException,
    isEscalated: leg.isEscalated,
    isUnderDiscussion: leg.isUnderDiscussion,
    isInternal: leg.isInternal,
    estimatedDistanceMiles: leg.estimatedDistanceMiles ?? undefined,
    invoiceDistanceMiles: leg.invoiceDistanceMiles ?? undefined,
    payableAmount: leg.payableAmount ?? undefined,
    tripLegStatusBC: leg.tripLegStatusBC,
    state: leg.tripLegState,
    status: leg.tripLegStatusBC?.toLowerCase(),
    overrides: [],
    transportationAttributes: [],
    tripLegPassengerNoteTypeBCs: [],
    tripLegDriverNoteTypeBCs: [],
    metadata: { roundTrip: false },
    pickUpAddress: mapTripLegAddress(
      leg.pickUpAddressLine1,
      leg.pickUpAddressLine2,
      leg.pickUpCity,
      leg.pickUpStateCode,
      leg.pickUpZipCode,
      leg.pickUpAddressLatitude,
      leg.pickUpAddressLongitude,
      leg.pickUpAddressTimeZoneIANA,
      leg.pickUpCounty,
    ),
    dropOffAddress: mapTripLegAddress(
      leg.dropOffAddressLine1,
      leg.dropOffAddressLine2,
      leg.dropOffCity,
      leg.dropOffStateCode,
      leg.dropOffZipCode,
      leg.dropOffAddressLatitude,
      leg.dropOffAddressLongitude,
      leg.dropOffAddressTimeZoneIANA,
      leg.dropOffCounty,
    ),
  };
}

export function mapTransportReservation(
  item: TransportReservation,
): Reservation {
  return {
    reservationReferenceID: item.reservationReferenceID,
    reservationServiceDate: item.reservationServiceDate,
    state: item.reservationState,
    clientBC: '',
    passengerUUID: item.passenger?.passengerUUID ?? '',
    tripLegs: item.tripLegs.map((leg) =>
      mapTransportTripLeg(leg, item.passenger),
    ),
  };
}

export function mapTransportReservationResponse(
  page: TransportReservationResponse,
): {
  reservations: Reservation[];
  continuationToken?: string;
} {
  const hasNextPage = page.currentPage < page.totalPages;
  return {
    reservations: page.items.map(mapTransportReservation),
    continuationToken: hasNextPage ? String(page.currentPage + 1) : undefined,
  };
}
